<template>
  <!-- スナップショット/フィールドノート SpeedDial: 右下に常駐、左に展開 -->
  <div v-if="showFab" class="snapshot-fab-container">
    <v-speed-dial
      v-model="isOpen"
      location="left center"
      transition="slide-x-reverse-transition"
    >
      <template #activator="{ props: activatorProps }">
        <v-btn
          v-bind="activatorProps"
          v-track="'snapshot-fab-toggle'"
          class="snapshot-fab"
          color="primary"
          size="x-large"
          icon
          elevation="8"
        >
          <MiniTooltip v-if="!isOpen" text="写真・保存" :offset-x="0" :offset-y="18">
            <v-icon size="32">mdi-camera</v-icon>
          </MiniTooltip>
          <v-icon v-else size="32" class="rotate-icon">mdi-close</v-icon>
        </v-btn>
      </template>

      <!-- 求積表OCR -->
      <v-btn
        v-track="'snapshot-fab-kyuseki-ocr'"
        class="snapshot-fab-item kyuseki-ocr"
        size="large"
        @click.stop="openKyusekiOcr"
      >
        <MiniTooltip text="求積表OCR" :offset-x="0" :offset-y="18">
          <span class="kyuseki-text">求積</span>
        </MiniTooltip>
      </v-btn>

      <!-- フィールドノート -->
      <v-btn
        v-track="'snapshot-fab-photo-ocr'"
        class="snapshot-fab-item photo-ocr"
        icon
        size="large"
        @click.stop="openPhotoOcr"
      >
        <MiniTooltip text="フィールドノート" :offset-x="0" :offset-y="18">
          <v-icon>mdi-camera</v-icon>
        </MiniTooltip>
      </v-btn>

      <!-- スナップショット -->
      <v-btn
        v-track="'snapshot-fab-snapshot'"
        class="snapshot-fab-item snapshot"
        icon
        size="large"
        @click.stop="openSaveDialog"
      >
        <MiniTooltip text="スナップショット" :offset-x="0" :offset-y="18">
          <v-icon>mdi-bookmark-plus</v-icon>
        </MiniTooltip>
      </v-btn>
    </v-speed-dial>
  </div>

  <!-- 名前入力ダイアログ -->
  <v-dialog v-model="saveDialog" max-width="400" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-bookmark-plus</v-icon>
        スナップショット
      </v-card-title>
      <v-card-text>
        <v-text-field
          ref="nameInput"
          v-model="snapshotName"
          label="スナップショット名"
          placeholder="例: 現場A調査"
          variant="outlined"
          density="comfortable"
          :rules="[v => !!v || '名前を入力してください']"
          @compositionstart="onCompositionStart"
          @compositionend="onCompositionEnd"
          @keydown.enter="onNameEnter"
        />

        <v-text-field
          v-model="snapshotTagsInput"
          label="タグ（カンマ区切り・任意）"
          placeholder="例: 現地調査, 河川, 南側"
          variant="outlined"
          density="comfortable"
          hide-details
        />

        <div v-if="allSnapshotTags.length > 0" class="mt-2">
          <div class="snapshot-tag-label">既存のタグをクリックで追加:</div>
          <v-chip
            v-for="tag in visibleSnapshotTags"
            :key="tag"
            size="small"
            :variant="isProtectionTag(tag) ? 'elevated' : 'outlined'"
            :color="isProtectionTag(tag) ? 'error' : undefined"
            class="mr-1 mb-1"
            style="cursor: pointer;"
            @click="addSnapshotTag(tag)"
          >{{ tag }}</v-chip>
          <v-chip v-if="!showAllSnapshotTags && hiddenSnapshotTagsCount > 0" size="small" variant="tonal" color="grey" class="mr-1 mb-1" style="cursor: pointer;" @click="showAllSnapshotTags = true">+{{ hiddenSnapshotTagsCount }}件</v-chip>
          <v-chip v-if="showAllSnapshotTags && allSnapshotTags.length > 10" size="small" variant="text" class="mr-1 mb-1" style="cursor: pointer;" @click="showAllSnapshotTags = false"><v-icon size="16">mdi-chevron-up</v-icon>閉じる</v-chip>
        </div>

        <v-textarea
          v-model="snapshotMemo"
          label="メモ（任意）"
          placeholder="このスナップショットに関するメモを入力"
          variant="outlined"
          density="compact"
          rows="2"
          hide-details
          class="mt-3"
        />

        <!-- ギャラリー公開設定 -->
        <v-divider class="mt-4 mb-2" />
        <v-switch
          v-model="galleryPublish"
          label="マップギャラリーに公開"
          color="primary"
          density="compact"
          hide-details
          class="mt-1"
        />
        <template v-if="galleryPublish">
          <v-select
            v-model="galleryCategory"
            :items="galleryCategories"
            item-title="label"
            item-value="value"
            label="カテゴリ"
            variant="outlined"
            density="compact"
            hide-details
            class="mt-2"
          />
          <v-textarea
            v-model="galleryDescription"
            label="説明文（ギャラリーに表示されます）"
            placeholder="この地図作品の説明を入力..."
            variant="outlined"
            density="compact"
            rows="2"
            counter="500"
            class="mt-2"
          />
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="closeDialog">キャンセル</v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="saving"
          :disabled="!snapshotName.trim()"
          @click="saveSnapshot"
        >
          保存
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import { showSuccess, showWarning, showLoginPrompt } from '@/js/utils/messageHelper'
import { consumeToken } from '@/js/tokenService'
import { addressFromMapCenter, japanCoord } from '@/js/downLoad'
import { createShortUrl } from '@/js/permalinkState'
import { cancelSessionSaveTimer } from '@/js/sessionState'
import MiniTooltip from '@/components/MiniTooltip.vue'

export default {
  name: 'SnapshotFab',
  components: { MiniTooltip },
  data() {
    return {
      isOpen: false, // SpeedDialの開閉状態
      saveDialog: false,
      snapshotName: '',
      saving: false,
      generatingTags: false,
      aiTags: [],
      aiTitle: '',
      aiDescription: '',
      aiError: false, // AIタグ生成エラーフラグ
      snapshotTagsInput: '',
      snapshotMemo: '', // メモ入力用
      snapshotTagItems: [],
      showAllSnapshotTags: false,
      isComposing: false,
      compositionJustEnded: false,
      // ギャラリー公開設定
      galleryPublish: false,
      galleryCategory: 'work',
      galleryDescription: '',
      galleryCategories: [
        { label: '業務・調査', value: 'work' },
        { label: '学習・教育', value: 'education' },
        { label: '趣味・その他', value: 'hobby' },
      ]
    }
  },
  computed: {
    ...mapState([
      'userId',
      'isPrint',
      'isDraw',
      'is4ScreenMode',
      'isIframe',
      'map2Flg'
    ]),
    showFab() {
      // 印刷モード、iframeでは非表示
      if (this.isPrint || this.isIframe) return false
      // ログインしていなくても表示（クリック時にログイン促進）
      return true
    },
    parsedSnapshotTags() {
      return this.parseSnapshotTagsInput(this.snapshotTagsInput)
    },
    allSnapshotTags() {
      const tagCount = {}
      this.snapshotTagItems.forEach(item => {
        if (item.tags) {
          try {
            const parsed = JSON.parse(item.tags)
            if (Array.isArray(parsed)) {
              parsed.forEach(tag => {
                tagCount[tag] = (tagCount[tag] || 0) + 1
              })
            }
          } catch (_) {
            // ignore
          }
        }
      })
      return Object.keys(tagCount).sort((a, b) => {
        const diff = tagCount[b] - tagCount[a]
        return diff !== 0 ? diff : a.localeCompare(b)
      })
    },
    visibleSnapshotTags() {
      if (this.showAllSnapshotTags) {
        return this.allSnapshotTags
      }
      return this.allSnapshotTags.slice(0, 10)
    },
    hiddenSnapshotTagsCount() {
      return Math.max(0, this.allSnapshotTags.length - 10)
    },
  },
  methods: {
    // 保護キーワードを含むタグかどうかをチェック
    isProtectionTag(tag) {
      if (!tag) return false
      const protectionKeywords = ['保護', 'ロック', 'プロテクト']
      return protectionKeywords.some(keyword => tag.includes(keyword))
    },
    getSnapshotMysqlBase() {
      return '/oh3/php'
    },
    // 求積表OCRを開く（App.vueの openKyusekiDialog と同じフロー）
    async openKyusekiOcr() {
      if (!this.userId) {
        showLoginPrompt({
          title: '求積表OCR',
          message: '求積表OCRはログインユーザー専用の機能です。<br>ご利用いただくには、ログインしてください。',
          icon: 'mdi-table-eye'
        })
        return
      }
      const r = await consumeToken('kyuseki_ocr')
      if (!r.success && r.error !== 'NETWORK_ERROR_IGNORED') return
      this.$root.openKyusekiDialog()
      this.isOpen = false
    },
    // フィールドノートを開く
    async openPhotoOcr() {
      // ログインチェック
      if (!this.userId) {
        showLoginPrompt({
          title: 'フィールドノート',
          message: 'フィールドノートはログインユーザー専用の機能です。<br>ご利用いただくには、ログインしてください。',
          icon: 'mdi-camera'
        })
        return
      }
      // トークン消費はPhotoMemoUpload内の保存時に行う（二重消費防止）
      this.$store.dispatch('showFloatingWindow', 'photo-memo-upload')
      this.isOpen = false // SpeedDialを閉じる
    },
    async openSaveDialog() {
      // ログインチェック
      if (!this.userId) {
        showLoginPrompt({
          title: 'スナップショット',
          message: 'スナップショットはログインユーザー専用の機能です。<br>ご利用いただくには、ログインしてください。',
          icon: 'mdi-bookmark-plus'
        })
        return
      }
      const r = await consumeToken('snapshot_save')
      if (!r.success && r.error !== 'NETWORK_ERROR_IGNORED') return

      // SpeedDialを閉じる
      this.isOpen = false

      // 初期表示は空（AIが返すまで）
      this.snapshotName = ''
      this.saveDialog = true

      // 画面中心の住所を取得して名称に反映（都道府県なし）
      this.prefillSnapshotNameFromCenter()
      // 既存タグを取得
      this.fetchSnapshotTags()
      // AIタグ生成は一旦停止
    },
    closeDialog() {
      this.saveDialog = false
      this.snapshotName = ''
      this.aiTags = []
      this.aiTitle = ''
      this.aiDescription = ''
      this.aiError = false
      this.snapshotTagsInput = ''
      this.snapshotMemo = ''
      this.snapshotTagItems = []
      this.showAllSnapshotTags = false
      this.isComposing = false
      this.compositionJustEnded = false
      this.galleryPublish = false
      this.galleryCategory = 'work'
      this.galleryDescription = ''
    },
    onCompositionStart() {
      this.isComposing = true
    },
    onCompositionEnd() {
      this.isComposing = false
      this.compositionJustEnded = true
      setTimeout(() => {
        this.compositionJustEnded = false
      }, 0)
    },
    onNameEnter(e) {
      if (this.isComposing || this.compositionJustEnded || e?.isComposing) return
      this.saveSnapshot()
    },
    prefillSnapshotNameFromCenter() {
      addressFromMapCenter()
      const maxWaitMs = 2000
      const intervalMs = 100
      let elapsed = 0
      const timer = setInterval(() => {
        elapsed += intervalMs
        const address = this.$store.state.addressNoPref || ''
        if (address) {
          if (!this.snapshotName) {
            this.snapshotName = address
          }
          clearInterval(timer)
        } else if (elapsed >= maxWaitMs) {
          clearInterval(timer)
        }
      }, intervalMs)
    },
    /// 画面中心のlng/latを現在のzahyokei(公共座標n系)で平面直角(X,Y)に変換
    /// store.state.zahyokei が空の場合は null を返す
    computeCenterJpCoord() {
      try {
        const map = this.$store.state.map01
        if (!map) return { zahyokei: null, x: null, y: null }
        const zkei = this.$store.state.zahyokei || ''
        if (!zkei) return { zahyokei: null, x: null, y: null }
        const c = map.getCenter()
        const jp = japanCoord([c.lng, c.lat])
        if (!jp || !Array.isArray(jp) || jp.length < 2) return { zahyokei: zkei, x: null, y: null }
        // proj4 は [東, 北]、日本測量座標系は X=北, Y=東 なので入替
        return { zahyokei: zkei, x: jp[1], y: jp[0] }
      } catch (e) {
        console.warn('computeCenterJpCoord error:', e)
        return { zahyokei: null, x: null, y: null }
      }
    },
    parseSnapshotTagsInput(input) {
      if (!input) return []
      const raw = input.split(/[,、]/).map((tag) => tag.trim()).filter((tag) => tag)
      return Array.from(new Set(raw))
    },
    addSnapshotTag(tag) {
      const next = this.parseSnapshotTagsInput(this.snapshotTagsInput)
      if (next.includes(tag)) {
        this.snapshotTagsInput = next.filter(t => t !== tag).join(', ')
        return
      }
      next.push(tag)
      this.snapshotTagsInput = next.join(', ')
    },
    async fetchSnapshotTags() {
      if (!this.userId) return
      try {
        const apiBase = this.getSnapshotMysqlBase()
        const fd = new FormData()
        fd.append('uid', this.userId)
        fd.append('isAll', 'false')
        const response = await fetch(`${apiBase}/userSnapshot.php`, { method: 'POST', body: fd })
        const data = await response.json()
        this.snapshotTagItems = Array.isArray(data) ? data : []
      } catch (_) {
        this.snapshotTagItems = []
      }
    },
    async saveSnapshot() {
      if (!this.snapshotName.trim()) {
        showWarning('名前を入力してください')
        return
      }

      this.saving = true
      try {
        // URL短縮完了を待つ
        const url = await this.waitForShortUrl()

        // スクリーンショットを撮影
        let thumbnailUrl = ''
        try {
          thumbnailUrl = await this.captureScreenshot()
        } catch (e) {
          console.warn('スクリーンショット取得失敗:', e)
          // スクリーンショット失敗してもスナップショット保存は続行
        }

        // 平面直角座標系とXY
        const { zahyokei: zkei, x: xVal, y: yVal } = this.computeCenterJpCoord()

        // サーバーに保存
        const formData = new FormData()
        formData.append('name', this.snapshotName.trim())
        formData.append('tags', JSON.stringify(this.parseSnapshotTagsInput(this.snapshotTagsInput)))
        formData.append('memo', this.snapshotMemo.trim())
        formData.append('url', url)
        formData.append('uid', this.userId)
        formData.append('thumbnail', thumbnailUrl)
        if (zkei) formData.append('zahyokei', zkei)
        if (xVal != null) formData.append('x', xVal.toFixed(3))
        if (yVal != null) formData.append('y', yVal.toFixed(3))

        const apiBase = this.getSnapshotMysqlBase()
        const response = await fetch(`${apiBase}/userSnapshot.php`, {
          method: 'POST',
          body: formData
        })

        const result = await response.json()
        if (result.error) {
          throw new Error(result.error)
        }

        // ギャラリー公開設定がONの場合、追加のupdateリクエスト
        if (this.galleryPublish && result.id) {
          try {
            const galleryForm = new FormData()
            galleryForm.append('action', 'update')
            galleryForm.append('id', result.id)
            galleryForm.append('is_gallery', '1')
            galleryForm.append('gallery_category', this.galleryCategory)
            galleryForm.append('gallery_description', this.galleryDescription.trim())
            await fetch(`${apiBase}/userSnapshot.php`, {
              method: 'POST',
              body: galleryForm
            })
          } catch (e) {
            console.warn('ギャラリー設定更新エラー:', e)
          }
        }

        const galleryMsg = this.galleryPublish ? '<br>マップギャラリーにも公開しました' : ''
        const urlDisplay = (url && url !== 'dumy' && url.startsWith('http')) ? `<br><span style="color: var(--oh3-color-text-secondary); word-break: break-all;">${url}</span>` : ''
        showSuccess(`「${this.snapshotName}」をマイルームに保存しました${galleryMsg}${urlDisplay}`)
        this.closeDialog()

        // 楽観的更新: POSTレスポンスのデータを即座にstoreにセット
        this.$store.state.snapshotNewItem = result

        // マイルームの簡単SSタブを更新するトリガー（DB読み直しの保険）
        this.$store.state.snapshotRefreshFire = !this.$store.state.snapshotRefreshFire

      } catch (error) {
        console.error('スナップショット保存エラー:', error)
        showWarning('保存に失敗しました: ' + error.message)
      } finally {
        this.saving = false
      }
    },
    // URL短縮完了を待つ
    waitForShortUrl() {
      return new Promise((resolve) => {
        const beforeUrl = this.$store.state.url

        // パーマリンク更新をトリガー → vm.param が生成される
        this.$store.state.updatePermalinkFire = !this.$store.state.updatePermalinkFire

        // 明示的にshortURL生成を呼ぶ（Dialog-shareと同じパターン）
        // updatePermalink()はvm.paramを生成するだけでshortURLは作らない
        this.$nextTick(() => {
          cancelSessionSaveTimer()
          setTimeout(() => createShortUrl(window.vueApp), 200)
        })

        // URLが変わり、かつ安定するまで待つ
        const maxWait = 5000 // 最大5秒（HTTP通信を待つため余裕を持たせる）
        const checkInterval = 100
        const stabilityWait = 500 // URL変化後、安定するまで待つ時間
        let elapsed = 0
        let lastUrl = beforeUrl
        let stableTime = 0

        const checkUrl = () => {
          elapsed += checkInterval
          const currentUrl = this.$store.state.url

          if (currentUrl !== lastUrl) {
            // URLが変わった - 安定カウンタをリセット
            lastUrl = currentUrl
            stableTime = 0
          } else if (currentUrl !== beforeUrl && currentUrl !== 'dumy' && currentUrl.startsWith('http')) {
            // URLは同じで、最初から変わっている、かつ有効なURL - 安定カウンタを増加
            stableTime += checkInterval
            if (stableTime >= stabilityWait) {
              resolve(currentUrl)
              return
            }
          }

          if (elapsed >= maxWait) {
            // タイムアウト: "dumy"や無効なURLの場合はwindow.location.hrefを使う
            const fallback = (currentUrl && currentUrl !== 'dumy' && currentUrl.startsWith('http'))
              ? currentUrl
              : window.location.href
            console.warn('[waitForShortUrl] タイムアウト。fallback:', fallback)
            resolve(fallback)
            return
          }

          setTimeout(checkUrl, checkInterval)
        }

        setTimeout(checkUrl, 400) // createShortUrl呼び出し後に十分な遅延
      })
    },
    async captureScreenshot() {
      const map01 = this.$store.state.map01
      if (!map01) throw new Error('地図が見つかりません')

      let sourceCanvas

      // 4画面モードの場合は4つのマップを合成
      if (this.is4ScreenMode) {
        const map02 = this.$store.state.map02
        const map03 = this.$store.state.map03
        const map04 = this.$store.state.map04

        const canvas01 = map01.getCanvas()
        const canvas02 = map02?.getCanvas()
        const canvas03 = map03?.getCanvas()
        const canvas04 = map04?.getCanvas()

        if (!canvas01) throw new Error('キャンバスが見つかりません')

        // 4画面合成用のcanvasを作成（2x2グリッド）
        const w = canvas01.width
        const h = canvas01.height
        const compositeCanvas = document.createElement('canvas')
        compositeCanvas.width = w * 2
        compositeCanvas.height = h * 2
        const ctx = compositeCanvas.getContext('2d')

        // 左上: map01
        ctx.drawImage(canvas01, 0, 0)
        // 右上: map02
        if (canvas02) ctx.drawImage(canvas02, w, 0)
        // 左下: map03
        if (canvas03) ctx.drawImage(canvas03, 0, h)
        // 右下: map04
        if (canvas04) ctx.drawImage(canvas04, w, h)

        sourceCanvas = compositeCanvas
      } else if (this.map2Flg) {
        // 2画面モードの場合はmap01とmap02を横並びで合成
        const map02 = this.$store.state.map02

        const canvas01 = map01.getCanvas()
        const canvas02 = map02?.getCanvas()

        if (!canvas01) throw new Error('キャンバスが見つかりません')

        // 2画面合成用のcanvasを作成（横並び）
        const w = canvas01.width
        const h = canvas01.height
        const compositeCanvas = document.createElement('canvas')
        compositeCanvas.width = w * 2
        compositeCanvas.height = h
        const ctx = compositeCanvas.getContext('2d')

        // 左: map01
        ctx.drawImage(canvas01, 0, 0)
        // 右: map02
        if (canvas02) ctx.drawImage(canvas02, w, 0)

        sourceCanvas = compositeCanvas
      } else {
        sourceCanvas = map01.getCanvas()
        if (!sourceCanvas) throw new Error('キャンバスが見つかりません')
      }

      // サムネイル用にリサイズ（幅200px程度）
      const targetWidth = 200
      const scale = targetWidth / sourceCanvas.width
      const targetHeight = Math.round(sourceCanvas.height * scale)

      const resizeCanvas = document.createElement('canvas')
      resizeCanvas.width = targetWidth
      resizeCanvas.height = targetHeight
      const ctx = resizeCanvas.getContext('2d')
      ctx.drawImage(sourceCanvas, 0, 0, targetWidth, targetHeight)

      const blob = await new Promise(resolve => resizeCanvas.toBlob(resolve, 'image/png', 0.8))
      if (!blob) throw new Error('Blob作成に失敗しました')

      const formData = new FormData()
      formData.append('file', blob, 'snapshot.png')

      const apiBase = this.getSnapshotMysqlBase()
      const response = await fetch(`${apiBase}/snapshotUpload.php`, {
        method: 'POST',
        body: formData
      })

      const result = await response.json()
      if (result.error) {
        throw new Error(result.error)
      }

      return result.thumbnail
    },
    // AIタグ生成
    async generateAiTags() {
      this.generatingTags = true
      this.aiTags = []
      this.aiError = false // エラー状態をリセット

      try {
        const map01 = this.$store.state.map01
        if (!map01) {
          showWarning('地図が見つかりません')
          return
        }

        // 現在の地図のスクリーンショットを取得（base64）
        const canvas = map01.getCanvas()
        if (!canvas) {
          showWarning('地図キャンバスが見つかりません')
          return
        }

        // 小さめにリサイズしてAPI呼び出しのコストを抑える
        const targetWidth = 300
        const scale = targetWidth / canvas.width
        const targetHeight = Math.round(canvas.height * scale)

        const resizeCanvas = document.createElement('canvas')
        resizeCanvas.width = targetWidth
        resizeCanvas.height = targetHeight
        const ctx = resizeCanvas.getContext('2d')
        ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight)

        const base64 = resizeCanvas.toDataURL('image/png').split(',')[1]

        // 位置情報を取得
        const center = map01.getCenter()
        const zoom = map01.getZoom()

        // 選択中の地番情報を取得（筆ID_地図名 形式から地図名部分を抽出）
        const highlightedChibans = this.$store.state.highlightedChibans
        let chibanInfo = null
        if (highlightedChibans && highlightedChibans.size > 0) {
          chibanInfo = Array.from(highlightedChibans)
            .slice(0, 10) // 最大10筆まで
            .map(id => {
              // 「筆ID_地図名」形式から地図名部分（最初のアンダースコア以降）を抽出
              const firstUnderscoreIndex = id.indexOf('_')
              return firstUnderscoreIndex !== -1 ? id.substring(firstUnderscoreIndex + 1) : id
            })
        }

        // Claude APIを呼び出し
        const response = await fetch('/oh3/php/claudeTag.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image_base64: base64,
            lat: center.lat,
            lng: center.lng,
            zoom: zoom,
            chiban: chibanInfo
          })
        })

        const result = await response.json()

        if (result.success) {
          this.aiTags = result.tags || []
          this.aiTitle = result.title || ''
          this.aiDescription = result.description || ''
          this.aiError = false

          // タイトルがあれば自動的に設定
          if (this.aiTitle) {
            this.snapshotName = this.aiTitle
          }

        } else {
          // APIエラー（上限到達など）→ 手動入力モードに切り替え
          console.warn('AI生成エラー:', result.error)
          this.aiError = true
          this.aiTags = []
          // ダイアログは開いたまま、ユーザーが手動で入力可能
        }
      } catch (error) {
        // ネットワークエラーなど → 手動入力モードに切り替え
        console.error('AIタグ生成エラー:', error)
        this.aiError = true
        this.aiTags = []
      } finally {
        this.generatingTags = false
      }
    }
  }
}
</script>

<style scoped>
.snapshot-fab-container {
  position: fixed;
  bottom: 80px;
  right: 20px;
  z-index: 1002; /* FreehandCanvas(1000)より上に配置 */
}

/* SpeedDialの背景を完全透明に、ぼかし削除 */
.snapshot-fab-container :deep(.v-speed-dial__content) {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

/* メニューオーバーレイの影も削除 */
.snapshot-fab-container :deep(.v-menu > .v-overlay__content) {
  box-shadow: none !important;
  border-radius: 0 !important;
}

.snapshot-fab {
  width: 64px !important;
  height: 64px !important;
  border-radius: var(--oh3-radius-full) !important;
  box-shadow:
    0 6px 20px rgba(var(--v-theme-primary), 0.4),
    0 3px 10px var(--oh3-color-overlay-20) !important;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
}

.snapshot-fab:hover {
  transform: scale(1.1) !important;
  box-shadow:
    0 8px 25px rgba(var(--v-theme-primary), 0.5),
    0 4px 15px rgba(0, 0, 0, 0.25) !important;
}

.snapshot-fab:active {
  transform: scale(0.95) !important;
}

.rotate-icon {
  transition: transform 0.3s ease;
}

/* SpeedDial アイテム共通 */
.snapshot-fab-item {
  width: 48px !important;
  height: 48px !important;
  color: white !important;
  transition: transform 0.2s ease !important;
}

.snapshot-fab-item:hover {
  transform: scale(1.15) !important;
}

/* 求積表OCR: テキストボタン - オレンジ系 */
.snapshot-fab-item.kyuseki-ocr {
  background: linear-gradient(135deg, #FF9800, #FFC107) !important;
  border-radius: var(--oh3-radius-full) !important;
  min-width: 48px !important;
  padding: 0 !important;
}

.kyuseki-text {
  font-size: var(--oh3-font-size-base);
  font-weight: 700;
  color: white;
  letter-spacing: 0.5px;
}

/* フィールドノート: カメラアイコン - 緑系 */
.snapshot-fab-item.photo-ocr {
  background: linear-gradient(135deg, #4CAF50, #8BC34A) !important;
}

/* スナップショット: ブックマークアイコン - 青系（primaryに近い色） */
.snapshot-fab-item.snapshot {
  background: linear-gradient(135deg, #2196F3, #03A9F4) !important;
}

/* スナップショット既存タグのラベル */
.snapshot-tag-label {
  font-size: var(--oh3-font-size-sm);
  color: var(--oh3-color-text-secondary);
  margin-bottom: var(--oh3-spacing-1);
}

/* 描画モード時は少し上に移動（draw-realtime-infoと重ならないように） */
.drawing-mode .snapshot-fab-container {
  bottom: 150px;
}

/* モバイル対応 */
@media (max-width: 500px) {
  .snapshot-fab-container {
    bottom: 70px;
    right: 16px;
  }

  .snapshot-fab {
    width: 56px !important;
    height: 56px !important;
  }

  .snapshot-fab-item {
    width: 44px !important;
    height: 44px !important;
  }
}

/* 印刷時は非表示 */
@media print {
  .snapshot-fab-container {
    display: none !important;
  }
}
</style>
